import React, { useCallback, useEffect, useRef } from "react";

import Button from "devextreme-react/button";
import DataGrid, {
  Column,
  Editing,
  FilterRow,
  Form,
  Grouping,
  GroupPanel,
  HeaderFilter,
  Item,
  LoadPanel,
  Paging,
  Selection,
  Sorting,
  Toolbar,
} from "devextreme-react/data-grid";
import { GroupItem, SimpleItem } from "devextreme-react/form";
import "devextreme-react/tag-box";
import "devextreme-react/text-area";
import CustomStore from "devextreme/data/custom_store";
import DataSource from "devextreme/data/data_source";
import { call, run, sleep } from "effection";
import { Case, Switch } from "react-context-switch";
import { useTranslation } from "react-i18next";

import { SideGroupTitle } from "@app/components/groupTitle";
import i18n from "@app/i18n";
import { ensureArray } from "@app/service/helpers";
import {
  selectAllGroupIdPerseed,
  selectAllGroupsIdxById,
  selectAllGroupsIdxPerseed,
  selectAllUserGroups,
  selectCurrentEmail,
  selectHomeDataOfSelectedUser,
  upsertGroupMember,
  useAppDispatch,
  useAppSelector,
} from "@app/state";
import * as CONSTANTS from "@app/state/constants";
import { isErr } from "@app/state/helpers/basic";

import "./userdef.scss";

import type { IAdminGroupUser } from "@app/types";
import type { DataChange } from "devextreme/common/grids";
import type { Operation } from "effection";

const t = i18n.t.bind(i18n);

const serverUrl_ =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_SERVERURL_PROD || ""
    : process.env.REACT_APP_SERVERURL_DEV || "";
const serverUrl = `${serverUrl_}${process.env.REACT_APP_PROD_SERVICE}`;
/* 
S - Super user
O - Owner (su)
F - Financial manager
L - Licence manager
A - IT Admin
U - User
 */
const CONST_SUPER = { id: "S", text: t("superuser") };
const CONST_OWNER = { id: "O", text: t("Owner") };
const CONST_FINANCIAL = { id: "F", text: t("finacial_manager") };
const CONST_LICENCE = { id: "L", text: t("licence_manager") };
const CONST_ADMIN = { id: "A", text: t("engineering") };
const CONST_USER = { id: "U", text: t("User") };
// insider, this is separate  //
const CONST_INSIDER = { id: "#1", text: t("insider") };

const rightsTagBox = {
  items: [
    CONST_SUPER,
    CONST_OWNER,
    CONST_FINANCIAL,
    CONST_LICENCE,
    CONST_ADMIN,
    CONST_USER,
  ],
};

const rightsTagBoxItemByKey = rightsTagBox.items.reduce(
  (acc: { [key: string]: { id: string; text: string } }, x) => {
    acc[x.id] = x;
    return acc;
  },
  {}
);

export const GroupMembership = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const thisAmaId = useRef<number | null>(null);
  const refGMGrid = useRef<any>(null);
  const fetchGuardRef = useRef<boolean>(false);
  const theUserGroups = useAppSelector(selectAllUserGroups);
  const theCCIdxByCode = useAppSelector(selectAllGroupsIdxPerseed);
  const theCCIdxById = useAppSelector(selectAllGroupsIdxById);
  const theCCodes = useAppSelector(selectAllGroupIdPerseed);
  const theEmail = useAppSelector(selectCurrentEmail) as string;
  const theHomeData = useAppSelector((s) =>
    selectHomeDataOfSelectedUser(s, theEmail)
  );
  const editRowKey = useRef<any>(null);
  const changes = useRef<DataChange[]>([]);

  useEffect(() => {
    changes.current = [];
    editRowKey.current = null;
  }, [theEmail]);

  const dataSource = new DataSource({
    store: new CustomStore({
      loadMode: "raw",
      key: "GROUPID_EMAIL",
      load: function () {
        return theUserGroups as IAdminGroupUser[];
      },
      insert: async function (values) {
        dispatch(upsertGroupMember(values));
        changes.current = [];
        editRowKey.current = null;
        return values;
      },
      update: async function (key, values) {
        const fullRow = theUserGroups.find(
          (x: IAdminGroupUser | undefined): x is IAdminGroupUser => {
            if (!x) return false;
            return x.GROUPID_EMAIL === key;
          }
        );
        if (!fullRow) return values;

        const newRow = { ...fullRow, ...values };
        dispatch(upsertGroupMember(newRow));
        changes.current = [];
        editRowKey.current = null;
        return values;
      },
      // remove: async function (key) {
      //   // dispatch(delete...);
      // },
    }),
  });
  const cellPrepared = (e: any) => {
    if (
      e.rowType === "data" &&
      // e.column.command &&
      //IS HOME GROUP, virtual, not database
      (e.data.GROUPID_EMAIL || "~").startsWith("~")
    ) {
      e.cellElement.classList.add("hidden-checkbox");
    }
  };
  const editorPreparing = useCallback((e: any) => {
    if (
      ["OUSER_EMAIL", "GROUPID"].includes(e.dataField) &&
      e.parentType === "dataRow" &&
      e.row.data?.GROUPID > 0
    ) {
      e.editorOptions.disabled = true; // readOnly = true;
    }
  }, []);

  const editingStart = (e: any) => {
    changes.current = [];
    editRowKey.current = e.data.GROUPID_EMAIL;
  };

  const newRowHandler = (e: { data: IAdminGroupUser }) => {
    e.data = {
      SEED: "JGAP",
      PERSEED: "",
      GROUPID: -1,
      AMAXAVOID: -1,
      OUSER_NAME: theHomeData.OUSER_NAME,
      OUSER_EMAIL: theEmail,
      OUSER_RIGHTS: "-U-",
      OUSER_IS_ACTIVE: 1,
      OUSER_STATUS: "OP_INSERT",
      OUSER_STATUSOP: new Date().toDateString(),
      GROUPID_EMAIL: `-1/${theEmail}`,
      //derived
      _OUSER_RIGHTS_JGAP: "-U-",
      _OUSER_RIGHTS_INSIDER: "",
    };
    editRowKey.current = `-1/${theEmail}`;
    changes.current = [];
  };

  const onRowValidating = (e: any) => {
    if (e.brokenRules?.length) {
      e.isValid = false;
      e.errorText = e.brokenRules.map((x: any) => x.message).join("\n");
      console.log("Validation Error 1:", e);
      return;
    }
    // mannual validation
    const newFullRow = { ...e.oldData, ...e.newData };
    if ((newFullRow?.PERSEED?.length ?? 0) < 2) {
      e.isValid = false;
      e.errorText = t("perSeedMustBe3Chars");
      console.log("Validation Error2:", e);
      return;
    }
    e.isValid = true;
    return;
  };

  const onSaving = (e: any) => {
    console.log("e", e);
    e.changes.forEach((ch: DataChange) => {
      if (ch.data["_OUSER_RIGHTS_INSIDER"] === true) {
        ch.data["_OUSER_RIGHTS_INSIDER"] = `-${CONST_INSIDER.id}-`;
      } else if (ch.data["_OUSER_RIGHTS_INSIDER"] === false) {
        ch.data["_OUSER_RIGHTS_INSIDER"] = "-";
      }
      if (Array.isArray(ch.data["_OUSER_RIGHTS_JGAP"]) === true) {
        ch.data["_OUSER_RIGHTS_JGAP"] = `-${ch.data["_OUSER_RIGHTS_JGAP"].join(
          "-"
        )}-`;
      }
    });
    console.log("e2", e);
  };

  const onRowPrepared = useCallback((e: any) => {
    if (e.rowType === "data") {
      if ((e.data?.GROUPID_EMAIL || "~").startsWith("~")) {
        e.rowElement.classList.add("dx-row-disabled");
      }
    }
  }, []);

  const calculateJgapDisplayValue = (e: any) => {
    return e._OUSER_RIGHTS_JGAP
      .split("-")
      .filter((x: any) => x.length > 0 && x !== CONST_INSIDER.id)
      .map((x: any) => rightsTagBoxItemByKey[x.trim()]?.text || "")
      .filter((x: any) => x.length > 0)
      .join(", ");
  };

  const calculateJgapRightsCellValue = (e: any) => {
    const ea =
      typeof e._OUSER_RIGHTS_JGAP === "string"
        ? e._OUSER_RIGHTS_JGAP.split("-")
        : ensureArray(e._OUSER_RIGHTS_JGAP);
    return ea
      .filter((x: any) => x.length > 0 && x !== CONST_INSIDER.id && x !== "-")
      .map((x: any) => x.trim());
  };

  const calculateInsidersCellValue = (e: any) => {
    switch (true) {
      case !(typeof e._OUSER_RIGHTS_INSIDER === "boolean"):
        if (theCCIdxByCode?.[e.PERSEED]?.INSIDERS_READY) {
          return (e._OUSER_RIGHTS_INSIDER || "-").includes(CONST_INSIDER.id);
        } else {
          return undefined;
        }
      case typeof e._OUSER_RIGHTS_INSIDER === "boolean":
      default:
        return e._OUSER_RIGHTS_INSIDER;
    }
  };

  function* effRequestAmaidSync(
    payload: IAdminGroupUser
  ): Operation<number | null> {
    yield* sleep(0);
    if (fetchGuardRef.current) {
      return null;
    }
    fetchGuardRef.current = true;
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");
    headers.append("Credentials", "include");
    headers.append("origin", window.location.origin);
    const { GROUPID_EMAIL } = payload || {};
    if (!GROUPID_EMAIL) return null;
    const url = `${serverUrl}/su/users/insidersync`;
    const response = yield* call(
      fetch(url, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({
          geml: GROUPID_EMAIL,
        }),
      })
    );
    const result = yield* call(response.json());
    if (result.err) {
      console.log("EROARE DE VALIDARE", result);
    }
    if (isErr(result)) {
      fetchGuardRef.current = false;
      return null;
    }
    const [{ OAMAID = null } = {}] = result.value;
    fetchGuardRef.current = false;
    return OAMAID;
  }

  const thisSetCellValue = (rowData: any, value: any, oldRowData: any) => {
    console.log("oldRowData", oldRowData);
    Object.assign(rowData, { AMAXAVOID: value });
    console.log("rowData", rowData);
    return rowData;
  };
  const groupIdSetCellValue = (rowData: any, value: any, oldRowData: any) => {
    const perseed = theCCIdxById[value]?.PERSEED;
    // rowData = {...rowData, ...oldRowData, GROUPID: value, PERSEED: perseed};
    Object.assign(rowData, { GROUPID: value, PERSEED: perseed });
    return rowData;
  };
  const oneLineThatChangesEverything = (f: any) => {
    const visibleRows = refGMGrid.current!.instance().getVisibleRows();
    const editingRow = visibleRows.find((x: any) => x.isEditing);
    const currentRow = editingRow.data;
    const isEnabled = theCCIdxByCode?.[currentRow.PERSEED]?.INSIDERS_READY;
    const msg =
      currentRow.AMAXAVOID > 0
        ? `${t("current_amaid", {
            amaid: currentRow.AMAXAVOID,
            ccode: currentRow.PERSEED,
          })}`
        : t("search_for_amaid_in_insiders", {
            email: currentRow.OUSER_EMAIL,
            ccode: currentRow.PERSEED,
          });

    return (
      <div>
        <Switch value={true}>
          <Case
            when={[() => isEnabled && !currentRow.PERSEED.startsWith("DEMO")]}
          >
            <div className="amax-wrap">
              <div className="amax-text">{msg}</div>
              <Button
                style={{ marginLeft: "auto", borderRadius: "3px" }}
                icon={currentRow.AMAXAVOID > 0 ? "refresh" : "search"}
                text={currentRow.AMAXAVOID > 0 ? "" : "scan"}
                stylingMode="outlined"
                type={currentRow.AMAXAVOID > 0 ? "success" : "default"}
                disabled={!isEnabled}
                onClick={async () => {
                  thisAmaId.current = null;
                  //this goes to the store but resets the grid already
                  // dispatch(getInsiderSyncAmaxavo(f.data.data));
                  const amaid = await run(() =>
                    effRequestAmaidSync(currentRow)
                  );
                  if (amaid) {
                    // Update the AMAXAVOID field using setCellValue
                    refGMGrid
                      .current!.instance()
                      .cellValue(editingRow.rowIndex, "AMAXAVOID", amaid);
                    // Now, refresh the grid
                    refGMGrid.current!.instance().refresh();
                  }
                }}
              />
            </div>
          </Case>
          <Case
            when={[() => isEnabled && currentRow.PERSEED.startsWith("DEMO")]}
          >
            <div className="amax-wrap">
              <div className="amax-text">{t("demo_will_impersonate")}</div>
            </div>
          </Case>
          <Case when={() => !isEnabled}>
            <div className="amax-wrap">
              <div className="amax-text">{t("not_available")}</div>
            </div>
          </Case>
        </Switch>
      </div>
    );
  };

  return (
    <div>
      <SideGroupTitle
        title={t("groupMembership")}
        sideKey={CONSTANTS.DRAWER_PAGES.INSIDERS}
      />

      <DataGrid
        id={"GroupMembers"}
        ref={refGMGrid}
        dataSource={dataSource}
        showBorders={true}
        showRowLines={true}
        showColumnLines={true}
        columnAutoWidth={false}
        allowColumnReordering={true}
        allowColumnResizing={true}
        autoNavigateToFocusedRow={true}
        focusedRowEnabled={false}
        filterSyncEnabled={true}
        repaintChangesOnly={true}
        renderAsync={undefined}
        wordWrapEnabled={true}
        noDataText={""}
        onEditorPreparing={editorPreparing}
        onCellPrepared={cellPrepared}
        onInitNewRow={newRowHandler}
        onRowPrepared={onRowPrepared}
        onRowValidating={onRowValidating}
        onEditingStart={editingStart}
        onSaving={onSaving}
      >
        <Editing
          mode={"form"}
          allowUpdating={true}
          allowDeleting={false}
          allowAdding={true}
        >
          <Form labelMode="floating" labelLocation="top" colCount={8}>
            <GroupItem colCount={8} colSpan={8} caption={t("jgap_only")}>
              <SimpleItem
                dataField={"GROUPID"}
                editorType={"dxSelectBox"}
                colSpan={4}
                editorOptions={{
                  elementAttr: {
                    id: "client-code-selectbox",
                  },
                  dataSource: theCCodes,
                  valueExpr: "GROUPID",
                  displayExpr: "PERSEED",
                  searchEnabled: true,
                }}
              />
              <SimpleItem
                dataField="OUSER_EMAIL"
                editorType={"dxTextBox"}
                colSpan={4}
              />
              <SimpleItem
                dataField="_OUSER_RIGHTS_JGAP"
                editorType={"dxTagBox"}
                colSpan={7}
                editorOptions={{
                  dataSource: rightsTagBox.items,
                  valueExpr: "id",
                  displayExpr: "text",
                  searchEnabled: true,
                }}
              ></SimpleItem>
              <SimpleItem
                dataField="OUSER_IS_ACTIVE"
                editorType={"dxCheckBox"}
                colSpan={1}
              />
            </GroupItem>
            <GroupItem colCount={8} colSpan={8} caption={t("insiders")}>
              <SimpleItem
                dataField="_OUSER_RIGHTS_INSIDER"
                editorType={"dxCheckBox"}
                colSpan={1}
              />
              <SimpleItem
                name="AMAXAVOID-2"
                dataField="AMAXAVOID"
                render={oneLineThatChangesEverything}
                colSpan={5}
              />
            </GroupItem>
          </Form>
        </Editing>
        <Sorting mode={"none"} />
        <Selection mode={"none"} />
        <Grouping autoExpandAll={false} />
        <GroupPanel visible={false} />
        <HeaderFilter visible={false} />
        <FilterRow visible={false} />
        <LoadPanel enabled={false} />
        <Paging enabled={false} />
        <Toolbar>
          <Item location="after" name="addRowButton" />
          <Item location="after" name="revertButton" />
          <Item location="after" name="saveButton" />
        </Toolbar>
        <Column
          dataField="GROUPID"
          caption={t("perSeed")}
          allowEditing={true}
          calculateDisplayValue={(e: any) => {
            return theCCIdxById[e.GROUPID]?.PERSEED || "";
          }}
          setCellValue={groupIdSetCellValue}
        />
        <Column
          dataField="PERSEED"
          caption={t("perSeed")}
          allowEditing={false}
          visible={false}
        />
        <Column
          dataField="OUSER_EMAIL"
          caption={t("email")}
          allowEditing={false}
          visible={false}
        />
        <Column
          dataField="_OUSER_RIGHTS_JGAP"
          caption={t("rights")}
          allowEditing={true}
          visible={true}
          calculateDisplayValue={calculateJgapDisplayValue}
          calculateCellValue={calculateJgapRightsCellValue}
        />
        <Column
          dataField="OUSER_IS_ACTIVE"
          dataType="boolean"
          caption={t("user_is_active")}
          allowEditing={true}
        />
        <Column
          dataField="OUSER_STATUS"
          caption={t("status")}
          allowEditing={false}
          visible={false}
        />
        <Column
          dataField="OUSER_STATUSOP"
          caption={t("statusOp")}
          allowEditing={false}
          visible={false}
        />
        <Column
          dataField="_OUSER_RIGHTS_INSIDER"
          dataType="boolean"
          caption={t("insider")}
          calculateCellValue={calculateInsidersCellValue}
          visible={true}
        />
        <Column
          dataField="AMAXAVOID"
          // dataType='boolean'
          caption={t("amaxavo")}
          allowEditing={false}
          setCellValue={thisSetCellValue}
          visible={false}
        />
      </DataGrid>
    </div>
  );
};
